import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const cloudData = {
  hero: {
    badge: "Cloud & DevOps",
    headline: "Cloud Infrastructure Built for Performance",
    subtext: "AWS deployment, Docker containerization, CI/CD pipelines and monitoring for scalable, reliable applications.",
    primaryCTA: "Optimize Your Infrastructure",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "Cloud Architecture", desc: "Well-architected solutions on AWS with high availability and fault tolerance.", icon: "ri-cloud-line" },
    { title: "AWS Deployment", desc: "EC2, ECS, EKS, Lambda and serverless deployment strategies.", icon: "ri-server-line" },
    { title: "Docker", desc: "Containerized applications with Docker Compose and Kubernetes orchestration.", icon: "ri-ship-line" },
    { title: "CI/CD", desc: "Automated build, test and deployment pipelines using GitHub Actions and AWS CodePipeline.", icon: "ri-refresh-line" },
    { title: "Cloudflare", desc: "CDN, DNS, DDoS protection and edge caching for global performance.", icon: "ri-shield-check-line" },
    { title: "Monitoring", desc: "Real-time monitoring with CloudWatch, Datadog and custom alerting.", icon: "ri-bar-chart-line" },
    { title: "Backups", desc: "Automated database and file backups with point-in-time recovery.", icon: "ri-archive-line" },
    { title: "Security", desc: "WAF, SSL, VPC, security groups and compliance hardening.", icon: "ri-lock-2-line" },
    { title: "Performance", desc: "Load balancing, auto-scaling and CDN for optimal response times.", icon: "ri-speed-line" },
    { title: "Infrastructure", desc: "Infrastructure as Code with Terraform and AWS CloudFormation.", icon: "ri-building-line" },
  ],
  techStack: [
    { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg", featured: true },
    { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
    { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-original.svg" },
    { name: "Terraform", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg" },
    { name: "GitHub Actions", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/githubactions/githubactions-original.svg" },
    { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg" },
    { name: "Linux", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg" },
    { name: "Nginx", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nginx/nginx-original.svg" },
    { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
  ],
  faq: [
    { q: "Which cloud platforms do you support?", a: "We primarily work with AWS but also support DigitalOcean, Google Cloud and Azure depending on your requirements." },
    { q: "Do you provide 24/7 monitoring?", a: "Yes. We set up comprehensive monitoring with automated alerts for uptime, performance, errors and security events." },
    { q: "Can you migrate existing infrastructure to AWS?", a: "Yes. We perform cloud migration assessments and execute zero-downtime migrations to AWS with full data integrity." },
    { q: "How do you handle disaster recovery?", a: "We implement multi-region backups, automated failover and documented recovery procedures with regular testing." },
  ],
};

export default function CloudDevopsPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "Cloud & DevOps" },
        ]}
      />

      <PageHero
        badge={cloudData.hero.badge}
        headline={cloudData.hero.headline}
        subtext={cloudData.hero.subtext}
        primaryCTA={cloudData.hero.primaryCTA}
        secondaryCTA={cloudData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">Cloud & DevOps Services</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {cloudData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">Technology Stack</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {cloudData.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className={`flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift ${tech.featured ? "ring-2 ring-accent-500/20" : ""}`}>
                  <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  <span className={`text-sm font-semibold ${tech.featured ? "text-accent-600" : "text-foreground-800"}`}>{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <FAQSection heading="Cloud & DevOps FAQ" items={cloudData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}